import { Fragment } from 'react';
import type { DayItem, POI, ScheduledPOI } from '@poi-planner/shared';
import type { TravelEstimate } from '../utils/geo';
import type { ConstraintViolation } from '../utils/constraints';
import { SortablePOICard } from './POICard';
import { TransportEventCard } from './TransportEventCard';
import { TravelIndicator } from './TravelIndicator';
import { dndIds } from '../utils/dndIds';

// ── Sub-Components ──

export function InsertionLine() {
  return (
    <div className="h-[3px] bg-blue-500 rounded my-0.5 shadow-[0_0_6px_rgba(59,130,246,0.4)] relative">
      <div className="absolute -left-1 -top-[3px] size-[9px] rounded-full bg-blue-500" />
      <div className="absolute -right-1 -top-[3px] size-[9px] rounded-full bg-blue-500" />
    </div>
  );
}

export function TimeOverrunIndicator({ overrunMinutes }: { overrunMinutes: number }) {
  return (
    <div className="flex items-center justify-center gap-1 my-1 px-2.5 py-1 bg-red-50 border border-red-200 rounded-md text-[11px] font-semibold text-red-600">
      <span className="material-symbols-outlined text-sm">warning</span>
      <span>{overrunMinutes}min overlap</span>
    </div>
  );
}

// ── Types ──

export type LayoutMode = 'kanban' | 'compact';

export interface TimeOverrun {
  poiId: string;
  overrunMinutes: number;
}

export interface TravelSegment {
  estimate: TravelEstimate | null;
}

export interface DayItemRendererProps {
  dayId: string;
  items: DayItem[];
  layout: LayoutMode;
  getPOI: (id: string) => POI | undefined;
  computedStartTimes: Map<string, string>;
  travelSegments: TravelSegment[];
  overrunByPoiId: Map<string, TimeOverrun>;
  violationsMap: Map<string, ConstraintViolation[]>;
  insertIndicator: number | null;
  totalPOIs: number; // For end-of-list insertion indicator
  /** Optional POI order mapping for numbered badges in day-focus mode */
  poiOrderById?: Map<string, number>;
  /** Callback when POI is selected (for map focus) */
  onSelectPoi?: (poiId: string | null) => void;
}

/**
 * Renders a list of day items (POIs and transport events).
 * Handles insertion indicators, travel indicators, and time overrun warnings.
 * Supports different layout modes for kanban vs compact views.
 */
export function DayItemRenderer({
  dayId,
  items,
  layout,
  getPOI,
  computedStartTimes,
  travelSegments,
  overrunByPoiId,
  violationsMap,
  insertIndicator,
  totalPOIs,
  poiOrderById,
  onSelectPoi,
}: DayItemRendererProps) {
  let poiIndex = 0;

  const isCompact = layout === 'compact';

  return (
    <div className={isCompact ? 'flex flex-col gap-0.5' : 'flex flex-col gap-0'}>
      {items.map((item, itemIndex) => {
        if (item.type === 'transport') {
          return (
            <Fragment key={`transport-${item.event.id}`}>
              <TransportEventCard event={item.event} />
              <TravelIndicator estimate={travelSegments[itemIndex + 1]?.estimate ?? null} />
            </Fragment>
          );
        }

        const poi = getPOI(item.poiId);
        if (!poi) return null;

        const currentPoiIndex = poiIndex++;
        const overrun = overrunByPoiId.get(poi.id);

        return (
          <Fragment key={dndIds.dayItem(dayId, item.poiId)}>
            {insertIndicator === currentPoiIndex && <InsertionLine />}
            <SortablePOICard
              id={dndIds.dayItem(dayId, item.poiId)}
              poi={poi}
              scheduledPOI={item.scheduledPOI}
              dayId={dayId}
              computedStartTime={computedStartTimes.get(poi.id)}
              violations={violationsMap.get(poi.id)}
              orderBadge={poiOrderById?.get(poi.id)}
              onSelectPoi={onSelectPoi}
            />
            {overrun && <TimeOverrunIndicator overrunMinutes={overrun.overrunMinutes} />}
            <TravelIndicator estimate={travelSegments[itemIndex + 1]?.estimate ?? null} />
          </Fragment>
        );
      })}
      {insertIndicator === totalPOIs && <InsertionLine />}
    </div>
  );
}

// ── Zone Renderer ──
// For transport-day gradients where items are split into origin/destination zones

export interface ZoneStyle extends React.CSSProperties {
  '--accom-bg'?: string;
  '--accom-header'?: string;
  '--accom-border'?: string;
  '--accom-text'?: string;
}

export interface ZonedDayItemsProps extends Omit<DayItemRendererProps, 'items'> {
  preTransportItems: DayItem[];
  postTransportItems: DayItem[];
  originPaletteVars: ZoneStyle;
  destPaletteVars: ZoneStyle;
  firstTransportIndex: number;
}

/**
 * Renders day items split into origin/destination zones for transport days.
 * Used when a day has transport events that transition between accommodations.
 */
export function ZonedDayItemRenderer({
  dayId,
  preTransportItems,
  postTransportItems,
  layout,
  getPOI,
  computedStartTimes,
  travelSegments,
  overrunByPoiId,
  violationsMap,
  insertIndicator,
  totalPOIs,
  poiOrderById,
  originPaletteVars,
  destPaletteVars,
  firstTransportIndex,
  onSelectPoi,
}: ZonedDayItemsProps) {
  let poiIndex = 0;

  const renderItems = (items: DayItem[], startItemIndex: number, paletteVars: ZoneStyle) => {
    return (
      <div style={{ ...paletteVars }}>
        {items.map((item, localIdx) => {
          const itemIndex = startItemIndex + localIdx;

          if (item.type === 'transport') {
            return (
              <Fragment key={`transport-${item.event.id}`}>
                <TransportEventCard event={item.event} />
                <TravelIndicator estimate={travelSegments[itemIndex + 1]?.estimate ?? null} />
              </Fragment>
            );
          }

          const poi = getPOI(item.poiId);
          if (!poi) return null;

          const currentPoiIndex = poiIndex++;
          const overrun = overrunByPoiId.get(poi.id);

          return (
            <Fragment key={dndIds.dayItem(dayId, item.poiId)}>
              {insertIndicator === currentPoiIndex && <InsertionLine />}
              <SortablePOICard
                id={dndIds.dayItem(dayId, item.poiId)}
                poi={poi}
                scheduledPOI={item.scheduledPOI}
                dayId={dayId}
                computedStartTime={computedStartTimes.get(poi.id)}
                violations={violationsMap.get(poi.id)}
                orderBadge={poiOrderById?.get(poi.id)}
                onSelectPoi={onSelectPoi}
              />
              {overrun && <TimeOverrunIndicator overrunMinutes={overrun.overrunMinutes} />}
              <TravelIndicator estimate={travelSegments[itemIndex + 1]?.estimate ?? null} />
            </Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className={layout === 'compact' ? 'flex flex-col gap-0.5' : 'flex flex-col gap-0'}>
      {/* Pre-transport zone (origin palette) */}
      {preTransportItems.length > 0 && renderItems(preTransportItems, 0, originPaletteVars)}

      {/* Post-transport zone (destination palette) — includes the transport event itself */}
      {renderItems(postTransportItems, firstTransportIndex, destPaletteVars)}

      {insertIndicator === totalPOIs && <InsertionLine />}
    </div>
  );
}
